// ✅ src/pages/VehicleV2/LubeConsumptionReport.jsx  (UPDATED WITH BACKEND APIs)
// Uses backend:
//   GET /api/lubeConsumptionReport/init
//   GET /api/lubeConsumptionReport/list
//
// Notes mapped from ASPX:
// - itemCategory fixed to 37 (locked from init)
// - branch lock based on branchType (HO => selectable, else locked)
// - suppliers come from init (ASPX checklist)
// - list calls: usp_LubricationConsumptionMonthWiseReport(branch, month, year, itemCategory, itemId, suppliersCsv, date)

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaInfoCircle,
  FaFileExport,
  FaEye,
} from "react-icons/fa";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

// ✅ If you already have axios baseURL configured, keep this empty:
const API_BASE = "https://nissi-india.com"; // e.g. ""

const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const isSelect = (v) => !toStr(v) || toStr(v).toLowerCase() === "select";

const LubeConsumptionReport = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [showFilters, setShowFilters] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // ✅ dropdown/meta
  const [metaLoading, setMetaLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [months, setMonths] = useState([]);
  const [years, setYears] = useState([]);
  const [itemCategory, setItemCategory] = useState({ id: "37", name: "LUBRICANTS", locked: true });
  const [itemNames, setItemNames] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [userLock, setUserLock] = useState({ branchLocked: false, branchValue: "" });

  // ✅ list data
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [total, setTotal] = useState(0);

  // ✅ suppliers selection (checkbox list)
  const [selectedSuppliers, setSelectedSuppliers] = useState([]); // array of supplier label text

  // ✅ filters (mapped to backend)
  const [filters, setFilters] = useState({
    branch: "Select",
    month: "Select", // backend expects month NUMBER as string "1..12"
    year: "Select",
    itemCategoryId: "37", // fixed
    itemId: "Select",
  });

  // 🔒 In your project you usually have these in session/user context.
  // For now, read from localStorage as a safe default (change if you store elsewhere).
  const branchType = toStr(localStorage.getItem("branchtype") || localStorage.getItem("branchType") || "HO");
  const loginBranchId = toStr(localStorage.getItem("branchid") || localStorage.getItem("branchId") || "");

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark ? "bg-gray-700/30 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
  }`;
  const labelClass = `block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const currentItems = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return rows.slice(indexOfFirstItem, indexOfLastItem);
  }, [rows, currentPage]);

  const totalPages = Math.ceil(rows.length / itemsPerPage) || 1;

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleChange = (e) => {
    setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const toggleSupplier = (label) => {
    setSelectedSuppliers((prev) => {
      if (prev.includes(label)) return prev.filter((x) => x !== label);
      return [...prev, label];
    });
  };

  // ✅ INIT: load dropdown/meta from backend
  const loadInit = async () => {
    try {
      setMetaLoading(true);

      const { data } = await axios.get(`${API_BASE}/api/lubeConsumptionReport/init`, {
        params: {
          branchType,
          branchId: loginBranchId || undefined,
        },
        // Optional headers if you use finyear switching:
        // headers: { "x-finyearid": 12 }
      });

      if (!data?.success) throw new Error(data?.message || "Init failed");

      setBranches(data.branches || []);
      setMonths(data.months || []);
      setYears(data.years || []);
      setItemCategory(data.itemCategory || { id: "37", name: "LUBRICANTS", locked: true });
      setItemNames(data.itemNames || []);
      setSuppliers(data.suppliers || []);
      setUserLock(data.userLock || { branchLocked: false, branchValue: "" });

      // Apply lock branch value (ASPX behavior)
      setFilters((p) => ({
        ...p,
        branch: data.userLock?.branchLocked ? (data.userLock.branchValue || "Select") : (p.branch || "Select"),
        itemCategoryId: (data.itemCategory?.id || "37"),
      }));
    } catch (err) {
      console.error("init error:", err);
      toast.error(err?.response?.data?.message || err.message || "Failed to load init data");
    } finally {
      setMetaLoading(false);
    }
  };

  useEffect(() => {
    loadInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ LIST: call backend list SP
  const fetchList = async (override = {}) => {
    const f = { ...filters, ...override };

    if (isSelect(f.month)) {
      toast.error("Please select Month");
      return;
    }
    if (isSelect(f.year)) {
      toast.error("Please select Year");
      return;
    }
    if (isSelect(f.branch)) {
      toast.error("Please select Branch");
      return;
    }

    try {
      setLoading(true);

      // Backend accepts suppliersCsv OR suppliers[]
      // We pass suppliers[] (array of labels) and backend will build CSV.
      const { data } = await axios.get(`${API_BASE}/api/lubeConsumptionReport/list`, {
        params: {
          branchId: f.branch,
          month: f.month, // IMPORTANT: month number string "1..12"
          year: f.year,
          itemCategoryId: f.itemCategoryId || "37",
          itemId: f.itemId || "Select",
          suppliers: selectedSuppliers, // array
          date: "", // ASPX passes empty
          // optional api pagination (we paginate in UI here)
          // page: 1,
          // pageSize: 500,
        },
      });

      if (!data?.success) throw new Error(data?.message || "List failed");

      setRows(data.rows || []);
      setColumns(data.columns || []);
      setTotal(data.total || 0);
      setCurrentPage(1);

      toast.success(`Found ${data.total ?? (data.rows || []).length} records`);
    } catch (err) {
      console.error("list error:", err);
      toast.error(err?.response?.data?.message || err.message || "Failed to fetch list");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchList();

  const handleViewAll = () => {
    // In ASPX, “view all” basically means run list with selected Month/Year/Branch but no extra filters.
    // Here itemCategory is fixed anyway; itemId => Select; suppliers => empty (means all).
    setFilters((p) => ({ ...p, itemId: "Select" }));
    setSelectedSuppliers([]);
    fetchList({ itemId: "Select" });
  };

  const handleClearFilters = () => {
    setFilters((p) => ({
      ...p,
      month: "Select",
      year: "Select",
      itemId: "Select",
      // branch keep if locked; else reset
      branch: userLock?.branchLocked ? (userLock.branchValue || "Select") : "Select",
      itemCategoryId: itemCategory?.id || "37",
    }));
    setSelectedSuppliers([]);
    toast.info("Filters cleared!");
  };

 const handlePrint = () => {
  const qs = new URLSearchParams({
    branchId: filters.branch,
    month: filters.month,
    year: filters.year,
    itemCategoryId: filters.itemCategoryId || "37",
    itemId: filters.itemId || "Select",
    // supplierCsv (optional)
    // supplier: selectedSuppliers.join(",") + ","
  }).toString();

  window.open(`/vehicle/LubeConsumptionReport_Print?${qs}`, "_blank");
};

  const handleExport = () => {
    // You can export from `rows` (client-side) or create backend excel/pdf endpoint.
    toast.success("Export: hook your CSV/XLSX export here");
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">LUBE CONSUMPTION REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white text-black`
          }`}
        >
          {/* Header Section */}
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Lube Consumption Report
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {loading ? "Loading..." : `${rows.length} Records`}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" + (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" + (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
               <button
                onClick={handlePrint}
                className="flex items-center gap-2 bg-black hover:bg-gray-900 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
              >
                <FaPrint />
                Print 
              </button>

              <button
                onClick={handleExport}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark ? "border-gray-600 text-white hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaFileExport className="w-4 h-4" />
                Export
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark ? "border-gray-600 text-white hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* Filters Section */}
          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}>
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                {/* Branch */}
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branch"
                    value={filters.branch}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!!userLock?.branchLocked}
                  >
                    <option value="Select">Select</option>
                    {branches.map((b) => (
                      <option key={toStr(b.value)} value={toStr(b.value)}>
                        {toStr(b.label)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Month */}
                <div>
                  <label className={labelClass}>Month</label>
                  <select name="month" value={filters.month} onChange={handleChange} className={inputClass}>
                    <option value="Select">Select</option>
                    {months.map((m) => (
                      <option key={toStr(m.value)} value={toStr(m.value)}>
                        {toStr(m.label)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Year */}
                <div>
                  <label className={labelClass}>Year</label>
                  <select name="year" value={filters.year} onChange={handleChange} className={inputClass}>
                    <option value="Select">Select</option>
                    {years.map((y) => (
                      <option key={toStr(y.value)} value={toStr(y.value)}>
                        {toStr(y.label)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Item Category (fixed to 37) */}
                <div>
                  <label className={labelClass}>Item Category</label>
                  <input
                    className={inputClass}
                    value={`${itemCategory?.name || "Item Category"} (ID: ${itemCategory?.id || "37"})`}
                    disabled
                  />
                </div>

                {/* Item Name */}
                <div>
                  <label className={labelClass}>Item Name</label>
                  <select name="itemId" value={filters.itemId} onChange={handleChange} className={inputClass}>
                    <option value="Select">Select</option>
                    {itemNames.map((it) => (
                      <option key={toStr(it.value)} value={toStr(it.value)}>
                        {toStr(it.label)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Suppliers checklist (ASPX ck_month) */}
              <div className="mb-6">
                <label className={labelClass}>Suppliers</label>
                <div
                  className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 p-3 rounded-lg border ${
                    isDark ? "border-gray-700 bg-gray-900/20" : "border-gray-200 bg-white"
                  }`}
                >
                  {suppliers.length === 0 ? (
                    <div className={`${isDark ? "text-gray-300" : "text-gray-600"} text-sm`}>
                      {metaLoading ? "Loading suppliers..." : "No suppliers"}
                    </div>
                  ) : (
                    suppliers.map((s) => {
                      const label = toStr(s.label);
                      const checked = selectedSuppliers.includes(label);
                      return (
                        <label key={label} className={`flex items-center gap-2 text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSupplier(label)}
                          />
                          {label}
                        </label>
                      );
                    })
                  )}
                </div>
                <div className={`text-xs mt-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Selected: {selectedSuppliers.length}
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleClearFilters}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
                >
                  Clear All Filters
                </button>
                <div className="flex gap-4">
                  <button
                    onClick={handleViewAll}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                    disabled={loading}
                  >
                    <FaEye />
                    View All
                  </button>
                  <button
                    onClick={handleSearch}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                    disabled={loading}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search Records"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Results Section */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {loading ? (
              <div className={`text-center py-16 ${isDark ? "text-gray-200" : "text-gray-700"}`}>Loading...</div>
            ) : rows.length === 0 ? (
              <div className="text-center py-16">
                <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${isDark ? "bg-gray-700" : "bg-gray-100"}`}>
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>No Records Found</h3>
                <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Select Month/Year and click Search.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {/* If SP returns dynamic columns, show those.
                    Otherwise you can keep your static columns table. */}
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      {(columns.length ? columns : Object.keys(currentItems[0] || {})).map((c) => (
                        <th key={c} className="px-4 py-4 text-center font-semibold">
                          {c}
                        </th>
                      ))}
                      <th className="px-4 py-4 text-center font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {currentItems.map((row, idx) => (
                      <tr
                        key={idx}
                        className={`${isDark ? "bg-gray-800" : "bg-white"} hover:${isDark ? "bg-gray-700" : "bg-gray-50"} transition-colors duration-200`}
                      >
                        {(columns.length ? columns : Object.keys(row)).map((c) => (
                          <td
                            key={c}
                            className={`px-4 py-4 whitespace-nowrap text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}
                          >
                            {toStr(row[c])}
                          </td>
                        ))}
                        <td className="px-4 py-4 text-center">
                          <button
                            onClick={handlePrint}
                            className={`p-2 rounded ${
                              isDark ? "bg-gray-700 text-white hover:bg-gray-600" : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                            }`}
                            title="Print"
                          >
                            <FaPrint className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {rows.length > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
              <div className={`text-sm mb-4 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, rows.length)} of {rows.length} entries
                {typeof total === "number" && total !== rows.length ? ` (Total: ${total})` : ""}
              </div>
              <nav className="flex gap-1">
                <button
                  onClick={() => paginate(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === 1
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Previous
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => paginate(pageNum)}
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === pageNum
                          ? `${buttonClass} text-white`
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === totalPages
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}

          <p className={`text-xs text-right mt-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}>INFRA SCREEN : B-60</p>

          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default LubeConsumptionReport;